<template>
<div class="blog-container" id="top">
  <button v-if="scrollPosition > 400" class="btn btn-secondary btn-floating">
    <a class="back-to-top-text" href="#top">Back to Top</a>
  </button>
  <JumbotronNav :title="blogTitle" class="nav-section"/>
  <div class="blog-content">
    <div v-for="post in blogPosts" :id="'post-' + post._id" :class="['row', 'page-bg', 'light-teal-bg', 'blog-post', {'highlighted-post': post._id === targetPostId}]">
      <div class="row">
        <div class="col-12">
          <h1>{{post.title}}</h1>
          <div class="metadata">
            <h5>{{formatDate(post.publishDate)}}</h5>
            <p v-if="post.author">By {{post.author}}</p>
            <button class="btn btn-link btn-sm copy-link-btn" @click="copyPostLink(post._id)">
              <font-awesome-icon icon="link"/>
              {{ copiedPostId === post._id ? 'Copied!' : 'Copy link' }}
            </button>
            <hr>
          </div>
          <MarkdownRenderer :source="post.text"/>
        </div>
        <div v-if="userIsAdmin" class="col-12 delete-post">
          <button class="btn btn-lg btn-danger float-right" @click="deletePost(post._id)">
            Delete Blog Post
            <font-awesome-icon icon="trash"/>
          </button>
          <button class="btn btn-lg btn-secondary float-right edit-post-btn" @click="editPost(post)">
            Edit Blog Post
            <font-awesome-icon icon="edit"/>
          </button>
        </div>
      </div>
    </div>
    <div v-if="resultsLoading" class="bottom-spinner text-center">
      <LoadingSpinner/>
    </div>
    <div v-else-if="!blogPosts.length" class="text-center p-4">
      <h2>No blog posts yet.</h2>
      <p>Please check back later for the latest articles from the Ancestral Homes Network team!</p>
    </div>
  </div>
  <div v-if="showLoadMoreButton" class="row">
    <div class="col-12 text-center">
      <button class="btn btn-lg btn-primary" @click="getBlogPosts({'nextPage': 1})">Load More</button>
    </div>
  </div>
</div>
</template>

<script>
import JumbotronNav from "@/components/JumbotronNav.vue";
import MarkdownRenderer from "@/components/MarkdownRenderer.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {mapActions, mapGetters} from "vuex";
import {submitStatus} from "@/helpers/utils";

export default {
  name: "Blog",
  components: {MarkdownRenderer, JumbotronNav, LoadingSpinner},
  data() {
    return {
      scrollPosition: 0,
      windowWidth: window.innerWidth,
      targetPostId: null,
      copiedPostId: null,
    }
  },
  created() {
    window.addEventListener('scroll', this.handleScroll);
  },
  mounted(){
    this.getBlogPosts({'nextPage': 0}).then(() => {
      const id = this.$route.params.id;
      if (id) {
        this.goToPost(id);
      }
    });
  },
  destroyed () {
    window.removeEventListener('scroll', this.handleScroll);
  },
  watch: {
    '$route.params.id'(id) {
      if (id) {
        this.goToPost(id);
      }
    }
  },
  computed: {
    ...mapGetters(['blogPosts', 'canLoadMore', 'blogStatus', 'userWithSettings']),
    resultsLoading() {
      return this.blogStatus === submitStatus.LOADING;
    },
    showLoadMoreButton(){
      return this.canLoadMore && !this.resultsLoading && this.blogPosts.length > 0;
    },
    user() {
      return this.userWithSettings;
    },
    userIsAdmin() {
      return this.user && this.user.is_admin;
    },
    screenIsSmall() {
      return this.windowWidth <= 600;
    },
    blogTitle(){
      return this.screenIsSmall ? 'Ancestral Homes Blog' : 'AncestralHomes Blog';
    }
  },
  methods: {
    ...mapActions(['getBlogPosts', 'deleteBlogPost', 'setPostToEdit']),
    handleScroll() {
      // Check whether we're at the top of the page,in which case don't show 'scroll to top' button
      this.scrollPosition = window.scrollY;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    deletePost(postId){
      this.deleteBlogPost(postId);
      location.reload();
    },
    editPost(post){
      this.setPostToEdit(post);
      this.$router.push({ name: 'addBlogPost' });
    },
    async goToPost(id){
      // Keep loading pages until the target post is loaded or there are no more to load.
      while (!this.blogPosts.some(post => post._id === id) && this.canLoadMore) {
        await this.getBlogPosts({'nextPage': 1});
      }
      await this.$nextTick();
      const element = document.getElementById('post-' + id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.targetPostId = id;
        setTimeout(() => {
          if (this.targetPostId === id) {
            this.targetPostId = null;
          }
        }, 3000);
      }
    },
    copyPostLink(id){
      const link = `${window.location.origin}/blog/${id}`;
      navigator.clipboard.writeText(link).then(() => {
        this.copiedPostId = id;
        setTimeout(() => {
          if (this.copiedPostId === id) {
            this.copiedPostId = null;
          }
        }, 2000);
      });
    }
  }
}
</script>

<style scoped>
.blog-post {
  padding: 2rem;
  margin-bottom: 2.5rem;
}
.blog-photo {
  float: right;
  max-width: 40%;
  height: auto;
  margin: 0 0 1rem 1.5rem;
}
.blog-container {
  padding: 1rem;
}
.blog-text {
  white-space: pre-wrap;
}
.bottom-spinner {
  margin: 2rem;
}
.delete-post {
  padding-top: 2rem;
}
.edit-post-btn {
  margin-left: 1rem;
}
.metadata {
  margin-bottom: 2rem;
}
.copy-link-btn {
  padding: 0;
  margin-bottom: 0.5rem;
  text-decoration: none;
  color: #006060;
}
.copy-link-btn:hover {
  color: #003536;
}
.blog-post {
  transition: box-shadow 0.4s ease, background-color 0.4s ease;
}
.highlighted-post {
  box-shadow: 0 0 0 3px #5a9;
}

@media screen and (max-width: 600px) {
  .blog-post {
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .blog-container {
    padding: 0.25rem;
  }
  .blog-photo {
    float: none;
    max-width: 100%;
    margin: 0 0 1rem 0;
  }
}
</style>